// The year a cost belongs to, for the cost list's year filter. A cost's
// invoice date is the year a broker or accountant files it under; its
// period is the year the cost is about. The cost list filters on the
// period, as a settlement selects its costs by it.

import { Invoice } from "./types";

export enum ECostYearBasis {
  InvoiceDate = "invoice_date",
  Period = "period",
}

// A cost period never spans more than this many years in the year options.
const MAX_PERIOD_YEARS = 10;

// The year of a cost's invoice date.
const costYear = (inv: Invoice): number =>
  Number((inv.date ?? "").slice(0, 4)) || 0;

const periodYearRange = (inv: Invoice): { from: number; to: number } => ({
  from: Number((inv.periodFrom ?? "").slice(0, 4)) || 0,
  to: Number((inv.periodTo ?? "").slice(0, 4)) || 0,
});

// Every year a cost's period touches (at most MAX_PERIOD_YEARS), oldest first.
const costPeriodYears = (inv: Invoice): number[] => {
  const { from, to } = periodYearRange(inv);
  const years: number[] = [];
  if (!from || !to) return years;
  for (
    let year = from;
    year <= to && year - from < MAX_PERIOD_YEARS;
    year += 1
  ) {
    years.push(year);
  }
  return years;
};

export const costInYear = (params: {
  invoice: Invoice;
  year: number;
  basis: ECostYearBasis;
}): boolean => {
  const { invoice, year, basis } = params;
  if (basis === ECostYearBasis.Period) {
    // The period overlaps the year, even for a single day.
    const { from, to } = periodYearRange(invoice);
    return from > 0 && to > 0 && from <= year && year <= to;
  }
  return costYear(invoice) === year;
};

// The years to choose from, newest first.
export const costYears = (params: {
  invoices: Invoice[];
  basis?: ECostYearBasis;
}): number[] => {
  const { invoices, basis = ECostYearBasis.InvoiceDate } = params;
  const years = invoices.flatMap(inv =>
    basis === ECostYearBasis.Period ? costPeriodYears(inv) : [costYear(inv)],
  );
  return [...new Set(years)].filter(year => year > 0).sort((a, b) => b - a);
};
